import { Link } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { CATEGORY_LIST } from "../lib/i18n";
import {
  FileText, FileSpreadsheet, Image as ImageIcon, Box, Zap, Video, Sticker,
  FileBadge, GraduationCap, Film, Folder, ArrowUpRight,
} from "lucide-react";

const ICONS = {
  FileText, FileSpreadsheet, Image: ImageIcon, Box, Zap, Video, Sticker, FileBadge,
  GraduationCap, Film, Folder,
};

function nameMap(t) {
  return {
    powerpoint: { name: t("powerpoint"), desc: t("powerpointDesc") },
    word: { name: t("word"), desc: t("wordDesc") },
    excel: { name: t("excel"), desc: t("excelDesc") },
    images: { name: t("images"), desc: t("imagesDesc") },
    "3d-icons": { name: t("threeDIcons"), desc: t("threeDIconsDesc") },
    icons: { name: t("icons"), desc: t("iconsDesc") },
    animation: { name: t("animation"), desc: t("animationDesc") },
    stickers: { name: t("stickers"), desc: t("stickersDesc") },
    cv: { name: t("cv"), desc: t("cvDesc") },
    school: { name: t("school"), desc: t("schoolDesc") },
    video: { name: t("video"), desc: t("videoDesc") },
    other: { name: t("other"), desc: t("otherDesc") },
  };
}

// Bento sizing — some tiles span 2 cols/rows
const SPAN = {
  powerpoint: "lg:col-span-2",
  images: "lg:col-span-2",
  "3d-icons": "lg:row-span-2",
  animation: "lg:col-span-2",
};

export default function CategoryGrid() {
  const { t } = useLang();
  const map = nameMap(t);

  return (
    <section className="py-20 sm:py-28" data-testid="category-grid-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid grid-cols-1 items-end gap-8 lg:grid-cols-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-fg">
              §02 — Categories
            </div>
            <h2 className="font-display mt-4 text-5xl leading-[0.95] sm:text-6xl">
              {t("twelveTypes").replace("12", "")}
              <br />
              <span className="font-serif text-accent">12 ways</span> to create.
            </h2>
          </div>
          <p className="max-w-md text-base text-muted-fg lg:text-end">{t("typesDesc")}</p>
        </div>

        <div className="mt-14 grid auto-rows-[180px] grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {CATEGORY_LIST.map((cat, idx) => {
            const Icon = ICONS[cat.icon] || Folder;
            const meta = map[cat.id] || { name: cat.id, desc: "" };
            const span = SPAN[cat.id] || "";
            return (
              <Link
                to={`/browse?category=${cat.id}`}
                key={cat.id}
                className={`group card card-hover relative flex flex-col justify-between overflow-hidden p-5 ${span}`}
                data-testid={`category-tile-${cat.id}`}
                style={{ minHeight: 180 }}
              >
                <div className="flex items-start justify-between">
                  <div
                    className="flex h-11 w-11 items-center justify-center rounded-xl text-white transition-transform group-hover:rotate-[-6deg] group-hover:scale-110"
                    style={{ backgroundColor: cat.color }}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="font-display text-xl text-muted-fg/40">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>
                <div>
                  <div className="font-display text-xl text-ink sm:text-2xl">{meta.name}</div>
                  <div className="mt-1 line-clamp-2 text-xs text-muted-fg">{meta.desc}</div>
                </div>
                <ArrowUpRight className="absolute end-4 bottom-4 h-4 w-4 text-muted-fg opacity-0 transition-all group-hover:opacity-100 group-hover:text-accent" />
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
